import React, { useEffect, useState } from 'react';
import MasterLayout from '../masterLayout/MasterLayout';
import Breadcrumb from '../components/Breadcrumb';
import { paymentsService, partnersService } from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react/dist/iconify.js';

const PaymentsListPage = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    partner_id: '',
    date_from: '',
    date_to: '',
    sortBy: 'payment_date',
    sortOrder: 'DESC'
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    loadPartners();
    loadPayments();
  }, [pagination.page, pagination.limit, filters]);

  const loadPartners = async () => {
    try {
      const response = await partnersService.getAllPartners({ 
        status: 'approved',
        limit: 1000 
      });
      if (response.success) {
        setPartners(response.data || []);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };

      // Boş filtreleri kaldır
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await paymentsService.getAllPayments(params);

      if (response.success) {
        setPayments(response.data || []);
        setPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          totalPages: response.pagination?.totalPages || 0
        }));
      } else {
        toast.error(response.error || 'Ödemeler yüklenemedi');
      }
    } catch (error) {
      console.error('Error loading payments:', error);
      toast.error('Ödemeler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentDetail = async (paymentId) => {
    try {
      setLoadingDetail(true);
      const response = await paymentsService.getPayment(paymentId);
      if (response.success) {
        setSelectedPayment(response.data);
        setShowDetailModal(true);
      } else {
        toast.error(response.error || 'Ödeme detayı alınamadı');
      }
    } catch (error) {
      console.error('Error loading payment detail:', error);
      toast.error('Ödeme detayı yüklenirken bir hata oluştu');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageSizeChange = (e) => {
    setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Ödemeler" />
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">Ödeme Listesi</h5>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/payments/send')}
          >
            <Icon icon="solar:wallet-money-outline" className="me-2" />
            Ödeme Gönder
          </button>
        </div>
        <div className="card-body">
          {/* Filtreler */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <label className="form-label">Partner</label>
              <select
                className="form-select"
                value={filters.partner_id}
                onChange={(e) => handleFilterChange('partner_id', e.target.value)}
              >
                <option value="">Tümü</option>
                {partners.map(partner => (
                  <option key={partner.id} value={partner.id}>
                    {partner.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Başlangıç Tarihi</label>
              <input
                type="date"
                className="form-control"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Bitiş Tarihi</label>
              <input
                type="date"
                className="form-control"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Sıralama</label>
              <select
                className="form-select"
                value={`${filters.sortBy}_${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('_');
                  setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                }}
              >
                <option value="payment_date_DESC">Tarih (Yeni → Eski)</option>
                <option value="payment_date_ASC">Tarih (Eski → Yeni)</option>
                <option value="total_amount_DESC">Tutar (Yüksek → Düşük)</option>
                <option value="total_amount_ASC">Tutar (Düşük → Yüksek)</option>
              </select>
            </div>
          </div>

          {/* Tablo */}
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
          ) : payments.length === 0 ? (
            <div className="alert alert-info text-center">
              Ödeme bulunamadı.
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Partner</th>
                      <th>Ödeme Tarihi</th>
                      <th>Toplam Tutar</th>
                      <th>Hak Ediş Sayısı</th>
                      <th>Oluşturulma</th>
                      <th className="text-end">İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(payment => (
                      <tr key={payment.id}>
                        <td>#{payment.id}</td>
                        <td>
                          <div>
                            <strong>{payment.partner_name}</strong>
                            {payment.partner_phone && (
                              <div className="text-muted small">{payment.partner_phone}</div>
                            )}
                          </div>
                        </td>
                        <td>{formatDate(payment.payment_date)}</td>
                        <td>
                          <strong className="text-success">{formatCurrency(payment.total_amount)}</strong>
                        </td>
                        <td>
                          <span className="badge bg-info">{payment.earning_count} adet</span>
                        </td>
                        <td>{formatDate(payment.created_at)}</td>
                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => loadPaymentDetail(payment.id)}
                          >
                            <Icon icon="solar:eye-outline" className="me-1" />
                            Detay
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center mt-4">
                <div>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: 'auto', display: 'inline-block' }}
                    value={pagination.limit}
                    onChange={handlePageSizeChange}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span className="ms-2 text-muted">
                    Toplam {pagination.total} kayıt
                  </span>
                </div>
                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                      >
                        Önceki
                      </button>
                    </li>
                    {[...Array(pagination.totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= pagination.page - 2 && page <= pagination.page + 2)
                      ) {
                        return (
                          <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      } else if (
                        page === pagination.page - 3 ||
                        page === pagination.page + 3
                      ) {
                        return (
                          <li key={page} className="page-item disabled">
                            <span className="page-link">...</span>
                          </li>
                        );
                      }
                      return null;
                    })}
                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        Sonraki
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Detay Modal */}
      {showDetailModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ödeme Detayı #{selectedPayment?.id}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedPayment(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {loadingDetail ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Yükleniyor...</span>
                    </div>
                  </div>
                ) : selectedPayment ? (
                  <>
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <h6>Partner Bilgileri</h6>
                        <p className="mb-1"><strong>Ad:</strong> {selectedPayment.partner_name}</p>
                        <p className="mb-1"><strong>Telefon:</strong> {selectedPayment.partner_phone}</p>
                        {selectedPayment.partner_email && (
                          <p className="mb-1"><strong>E-posta:</strong> {selectedPayment.partner_email}</p>
                        )}
                      </div>
                      <div className="col-md-6">
                        <h6>Ödeme Bilgileri</h6>
                        <p className="mb-1"><strong>Ödeme Tarihi:</strong> {formatDate(selectedPayment.payment_date)}</p>
                        <p className="mb-1"><strong>Toplam Tutar:</strong> <span className="text-success fw-bold">{formatCurrency(selectedPayment.total_amount)}</span></p>
                        <p className="mb-1"><strong>Oluşturulma:</strong> {formatDate(selectedPayment.created_at)}</p>
                        {selectedPayment.created_by_name && (
                          <p className="mb-1"><strong>Oluşturan:</strong> {selectedPayment.created_by_name}</p>
                        )}
                      </div>
                    </div>
                    {selectedPayment.notes && (
                      <div className="mb-4">
                        <h6>Notlar</h6>
                        <p className="text-muted">{selectedPayment.notes}</p>
                      </div>
                    )}
                    {selectedPayment.earnings && selectedPayment.earnings.length > 0 && (
                      <div>
                        <h6>Hak Edişler ({selectedPayment.earnings.length} adet)</h6>
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Müşteri</th>
                                <th>Satış Tarihi</th>
                                <th className="text-end">Net Tutar</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedPayment.earnings.map(earning => (
                                <tr key={earning.id}>
                                  <td>
                                    {earning.customer_name || '-'}
                                    {earning.customer_phone && (
                                      <div className="text-muted small">{earning.customer_phone}</div>
                                    )}
                                  </td>
                                  <td>{formatDate(earning.sale_date)}</td>
                                  <td className="text-end">{formatCurrency(earning.net_amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <th colSpan="2">Toplam</th>
                                <th className="text-end">{formatCurrency(selectedPayment.total_amount)}</th>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDetailModal(false);
                    setSelectedPayment(null);
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => {
            setShowDetailModal(false);
            setSelectedPayment(null);
          }}></div>
        </div>
      )}
    </MasterLayout>
  );
};

export default PaymentsListPage;

